import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, OnDestroy, OnInit, inject, signal } from '@angular/core';
import { FormBuilder, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { ButtonModule } from 'primeng/button';
import { ConfirmDialogModule } from 'primeng/confirmdialog';
import { DialogModule } from 'primeng/dialog';
import { InputTextModule } from 'primeng/inputtext';
import { ConfirmationService, MessageService, MenuItem } from 'primeng/api';
import { SelectModule } from 'primeng/select';
import { Toast } from '@/shared/components/toast/toast';
import { TablePageEvent } from 'primeng/table';
import { CheckboxModule } from 'primeng/checkbox';
import { forkJoin, Observable, Subscription } from 'rxjs';
import { CardContent } from '@/shared/components/card-content/card-content';

import {
    ComissaoAtividadeFiltroRequest,
    ComissaoAtividadeResponse,
    ComissaoAtividadeResponsavelFiltroRequest,
    ComissaoAtividadeResponsavelResponse,
    EComissaoAtividadeTipoPesquisaFiltro,
    ComissaoAtividadeSalvarRequest,
    ComissaoSituacaoAtividadeResponse
} from '@/core/api/sicom-api/comissoes/atividades/comissao-atividade.model';
import { ComissaoAtividadeService } from '@/core/api/sicom-api/comissoes/atividades/comissao-atividade.service';
import { ComissaoEstatisticaService } from '@/core/api/sicom-api/comissoes/estatisticas/comissao-estatistica.service';
import { ComissaoEstatisticaAgrupadaResponse } from '@/core/api/sicom-api/comissoes/estatisticas/comissao-estatistica.model';
import { ComissaoEstruturaFiltroResponse, ComissaoTipoComissaoResponse } from '@/core/api/sicom-api/comissoes/reunioes/comissao-reuniao.model';
import { ComissaoReuniaoService } from '@/core/api/sicom-api/comissoes/reunioes/comissao-reuniao.service';
import { ProtocoloResponse, ProtocoloSelecaoResponse, ProtocoloTipoResponse } from '@/core/api/sicom-api/protocolos/protocolo.model';
import { ProtocoloService } from '@/core/api/sicom-api/protocolos/protocolo.service';
import { AtividadeComissao, FiltrosAtividadesComissoes, TipoSelecaoAtividade, criarFiltrosAtividadesIniciais } from '@/features/comissoes/dashboard-comissoes/atividades-comissoes.model';
import { FiltrosAtividadesComissoesComponent } from '@/features/comissoes/dashboard-comissoes/components/filtros-atividades-comissoes/filtros-atividades-comissoes';
import { SeletorArvoreComissaoComponent } from '@/features/comissoes/dashboard-comissoes/components/seletor-arvore-comissao/seletor-arvore-comissao';
import { AcaoTabelaAtividadesComissoes, TabelaAtividadesComissoesComponent } from '@/features/comissoes/dashboard-comissoes/components/tabela-atividades-comissoes/tabela-atividades-comissoes';
import { Card } from 'primeng/card';
import { CanDirective } from '@/core/permissions/can.directive';
import { PermissionService } from '@/core/permissions/permission.service';
import { PERMISSOES } from '@/core/permissions/permissoes.catalog';
import { AppMenuItem } from '@/core/permissions/permission.model';

import { DialogoPadraoDirective } from '@/shared/components/layout/dialogo-padrao/dialogo-padrao.directive';
type AcaoOpcaoAtividade = 'cadastrar' | 'relatorio';
type CampoOrdenacaoProtocolo = 'nuProtocolo' | 'nuAnoProtocolo' | 'dsTipoProtocolo';

@Component({
    selector: 'app-dashboard-atividades-comissoes',
    standalone: true,
    imports: [
        DialogoPadraoDirective,
        CommonModule,
        FormsModule,
        ReactiveFormsModule,
        ButtonModule,
        DialogModule,
        SelectModule,
        InputTextModule,
        ConfirmDialogModule,
        Toast,
        FiltrosAtividadesComissoesComponent,
        SeletorArvoreComissaoComponent,
        TabelaAtividadesComissoesComponent,
        CheckboxModule,
        CardContent,
        Card,
        CanDirective
    ],
    providers: [ConfirmationService, MessageService],
    host: { '(window:resize)': 'aoRedimensionarJanela()' },
    templateUrl: './dashboard-atividades-comissoes.html',
    styleUrls: ['./dashboard-atividades-comissoes.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class DashboardAtividadesComissoesComponent implements OnInit, OnDestroy {
    private readonly confirmationService = inject(ConfirmationService);
    private readonly messageService = inject(MessageService);
    private readonly cdr = inject(ChangeDetectorRef);
    private readonly permissionService = inject(PermissionService);

    private telaEstreitaFiltros = typeof window !== 'undefined' && window.innerWidth <= 1200;
    /**
     * Os filtros começam ocultos: a tela abre direto na lista, e a coluna lateral só entra
     * quando o usuário pede pelo botão de filtro. O estado é local e não persiste entre
     * navegações.
     */
    readonly filtrosVisiveis = signal(false);
    carregandoAtividades = false;

    /** Permissão exigida no botão "Salvar Atividade" ao editar uma atividade existente. */
    protected readonly roleEditarAtividade = PERMISSOES.comissoes.atividades.editar;
    /** Permissão exigida no botão "Salvar Atividade" ao cadastrar uma nova atividade. */
    protected readonly roleCriarAtividade = PERMISSOES.comissoes.atividades.criar;

    /** Permissão do botão "Salvar Atividade": criar quando não há atividade carregada, senão editar. */
    protected get roleSalvarAtividade(): string {
        return this.formularioAtividade.controls.idTarefa.value ? this.roleEditarAtividade : this.roleCriarAtividade;
    }

    private _erroConsulta = '';
    get erroConsulta(): string {
        return this._erroConsulta;
    }
    set erroConsulta(value: string) {
        this._erroConsulta = value;
        if (value) {
            this.messageService.add({ severity: 'error', summary: 'Erro', detail: value });
        }
    }

    filtrosAtivos: FiltrosAtividadesComissoes = criarFiltrosAtividadesIniciais();

    atividadeSelecionada: AtividadeComissao | null = null;
    atividadesBase: AtividadeComissao[] = [];
    atividadesFiltradas: AtividadeComissao[] = [];
    /**
     * Contagem por situação do conjunto inteiro, no mesmo filtro da listagem — é o que alimenta os
     * indicadores "Pendentes"/"Resolvidas". Nulo enquanto a consulta não responde, para a tabela
     * mostrar "–" em vez de um número da página.
     */
    situacoesResumoAtividades: ComissaoEstatisticaAgrupadaResponse[] | null = null;
    totalRegistrosAtividades = 0;
    primeiraLinhaAtividades = 0;
    linhasPorPaginaAtividades = 20;

    private _ultimaAcaoExecutada = '';
    get ultimaAcaoExecutada(): string {
        return this._ultimaAcaoExecutada;
    }
    set ultimaAcaoExecutada(value: string) {
        this._ultimaAcaoExecutada = value;
        if (value) {
            const isError = value.toLowerCase().includes('nao') || value.toLowerCase().includes('não') || value.toLowerCase().includes('erro');
            this.messageService.add({
                severity: isError ? 'error' : 'success',
                summary: isError ? 'Erro' : 'Sucesso',
                detail: value
            });
        }
    }
    responsaveisPesquisa: ComissaoAtividadeResponsavelResponse[] = [];
    carregandoResponsaveisPesquisa = false;
    erroPesquisaResponsaveis = '';

    private consultaAtividadesSub?: Subscription;
    private consultaSituacoesResumoSub?: Subscription;
    private consultaResponsaveisSub?: Subscription;
    private excluirAtividadeSub?: Subscription;
    private exportarAtividadesSub?: Subscription;
    private consultaProtocoloSub?: Subscription;
    private consultaTiposProtocoloSub?: Subscription;
    private routeParamsSub?: Subscription;

    exibirDialogAtividade = false;
    tituloDialogAtividade = '';
    tiposComissao: ComissaoTipoComissaoResponse[] = [];
    estruturasComissao: ComissaoEstruturaFiltroResponse[] = [];
    situacoesAtividade: ComissaoSituacaoAtividadeResponse[] = [];
    carregandoEstruturasComissao = false;
    carregandoSituacoesAtividade = false;
    erroEstruturasComissao = '';
    exibirDialogResponsaveis = false;
    filtroResponsavelUf = 'BR';
    filtroResponsavelNome = '';
    responsaveisCadastro: ComissaoAtividadeResponsavelResponse[] = [];
    carregandoResponsaveisCadastro = false;
    erroPesquisaResponsaveisCadastro = '';
    protocoloAtividadeSelecionado = '';
    erroPesquisaProtocoloAtividade = '';
    exibirDialogProtocolos = false;
    tiposProtocolo: ProtocoloTipoResponse[] = [];
    filtroTipoProtocolo = 0;
    filtroNuProtocolo = '';
    filtroAnoProtocolo = '';
    protocolosPesquisa: ProtocoloSelecaoResponse[] = [];
    protocoloPesquisaSelecionado: ProtocoloSelecaoResponse | null = null;
    carregandoTiposProtocolo = false;
    carregandoProtocolos = false;
    ordenacaoProtocoloCampo: CampoOrdenacaoProtocolo = 'nuProtocolo';
    ordenacaoProtocoloDesc = false;
    proximoCodigoAtividade = '';

    private readonly formBuilder = inject(FormBuilder);

    readonly formularioAtividade = this.formBuilder.nonNullable.group({
        idTarefa: [null as number | null],
        idEstruturaOrganizacional: [null as number | null],
        idResponsavel: [null as number | null],
        sgUfResponsavel: [''],
        nomeResponsavel: [''],
        descricao: [''],
        observacao: [''],
        dataInicio: [''],
        dataFim: [''],
        idSituacaoTarefa: [null as number | null],
        idReuniao: [null as number | null],
        idProtocolo: [null as number | null],
        resolvida: [false]
    });

    constructor(
        private readonly route: ActivatedRoute,
        private readonly router: Router,
        private readonly comissaoReuniaoService: ComissaoReuniaoService,
        private readonly comissaoAtividadeService: ComissaoAtividadeService,
        private readonly comissaoEstatisticaService: ComissaoEstatisticaService,
        private readonly protocoloService: ProtocoloService
    ) {}

    ngOnInit(): void {
        this.carregarEstruturasComissao();
        this.carregarSituacoesAtividade();

        this.routeParamsSub = this.route.queryParams.subscribe((queryParams) => {
            const reuniaoIdStr = queryParams['reuniao'];
            const novaAtividadeStr = queryParams['novaAtividade'];

            if (reuniaoIdStr) {
                const idReuniao = Number(reuniaoIdStr);
                if (Number.isFinite(idReuniao) && idReuniao > 0) {
                    this.filtrosAtivos.tipoSelecao = 'codigo-reuniao';
                    this.filtrosAtivos.valorSelecao = String(idReuniao);
                }
            }

            this.carregarAtividades(this.filtrosAtivos, this.paginaAtual(), this.linhasPorPaginaAtividades);
            this.cdr.markForCheck();

            if (novaAtividadeStr === 'true') {
                this.router.navigate([], {
                    relativeTo: this.route,
                    queryParams: { novaAtividade: null },
                    queryParamsHandling: 'merge'
                });

                if (this.permissionService.can(PERMISSOES.comissoes.atividades.criar)) {
                    setTimeout(() => {
                        const idReuniao = Number(reuniaoIdStr);
                        this.abrirDialogNovaAtividade(Number.isFinite(idReuniao) && idReuniao > 0 ? idReuniao : null);
                        this.cdr.markForCheck();
                    }, 100);
                }
            }
        });
    }

    ngOnDestroy(): void {
        this.consultaAtividadesSub?.unsubscribe();
        this.consultaSituacoesResumoSub?.unsubscribe();
        this.consultaResponsaveisSub?.unsubscribe();
        this.excluirAtividadeSub?.unsubscribe();
        this.exportarAtividadesSub?.unsubscribe();
        this.consultaProtocoloSub?.unsubscribe();
        this.consultaTiposProtocoloSub?.unsubscribe();
        this.routeParamsSub?.unsubscribe();
    }

    carregarEstruturasComissao(): void {
        this.carregandoEstruturasComissao = true;
        this.erroEstruturasComissao = '';

        forkJoin({
            tipos: this.comissaoReuniaoService.listarTiposComissao(),
            estruturas: this.comissaoReuniaoService.listarFiltrosComissao(0)
        }).subscribe({
            next: ({ tipos, estruturas }) => {
                this.tiposComissao = tipos ?? [];
                this.estruturasComissao = estruturas ?? [];
                this.carregandoEstruturasComissao = false;
                this.cdr.markForCheck();
            },
            error: () => {
                this.tiposComissao = [];
                this.estruturasComissao = [];
                this.erroEstruturasComissao = 'Não foi possível carregar as comissões.';
                this.carregandoEstruturasComissao = false;
                this.cdr.markForCheck();
            }
        });
    }

    carregarSituacoesAtividade(): void {
        this.carregandoSituacoesAtividade = true;
        this.comissaoAtividadeService.listarSituacoesAtividade().subscribe({
            next: (situacoes) => {
                this.situacoesAtividade = situacoes ?? [];
                this.carregandoSituacoesAtividade = false;
                this.cdr.markForCheck();
            },
            error: () => {
                this.situacoesAtividade = [];
                this.carregandoSituacoesAtividade = false;
                this.messageService.add({ severity: 'error', summary: 'Erro', detail: 'Não foi possível carregar as situações da atividade.' });
                this.cdr.markForCheck();
            }
        });
    }

    aoAplicarFiltros(filtros: FiltrosAtividadesComissoes): void {
        this.reiniciarPaginacaoAtividades();
        this.carregarAtividades(filtros, this.paginaAtual(), this.linhasPorPaginaAtividades);
    }

    aoLimparFiltros(): void {
        this.filtrosAtivos = criarFiltrosAtividadesIniciais();
        this.atividadeSelecionada = null;
        this.responsaveisPesquisa = [];
        this.erroPesquisaResponsaveis = '';
        this.reiniciarPaginacaoAtividades();
        this.carregarAtividades(this.filtrosAtivos, this.paginaAtual(), this.linhasPorPaginaAtividades);
    }

    aoPesquisarResponsaveis(request: ComissaoAtividadeResponsavelFiltroRequest): void {
        this.carregandoResponsaveisPesquisa = true;
        this.erroPesquisaResponsaveis = '';

        this.consultaResponsaveisSub?.unsubscribe();
        this.consultaResponsaveisSub = this.comissaoAtividadeService.listarResponsaveisAtividade(request).subscribe({
            next: (responsaveis) => {
                this.responsaveisPesquisa = responsaveis ?? [];
                this.carregandoResponsaveisPesquisa = false;
                this.cdr.markForCheck();
            },
            error: () => {
                this.responsaveisPesquisa = [];
                this.erroPesquisaResponsaveis = 'Não foi possível carregar os responsáveis.';
                this.carregandoResponsaveisPesquisa = false;
                this.cdr.markForCheck();
            }
        });
    }

    aoSelecionarAtividade(atividade: AtividadeComissao | null): void {
        this.atividadeSelecionada = atividade;
    }

    aoMudarPaginaAtividades(event: TablePageEvent): void {
        const primeiraLinha = this.normalizarInteiroNaoNegativo(event.first);
        const linhas = this.normalizarLinhasPorPagina(event.rows);

        this.primeiraLinhaAtividades = primeiraLinha;
        this.linhasPorPaginaAtividades = linhas;
        this.carregarAtividades(this.filtrosAtivos, this.paginaAtual(), this.linhasPorPaginaAtividades);
    }

    itensOpcoes(): AppMenuItem[] {
        return [
            {
                label: 'Cadastrar',
                icon: 'pi pi-plus',
                styleClass: 'bg-green-500',
                permission: PERMISSOES.comissoes.atividades.criar,
                command: () => this.aoExecutarOpcao('cadastrar')
            },
            {
                label: 'Relatório',
                icon: 'pi pi-file',
                styleClass: 'bg-teal-500',
                // Exportar atividades cai no bloco backend /comissoes/atividades/** (POST sem methodRoles),
                // liberado a qualquer role de atividades — a role de gate tem que ser a mesma (visualizar), nao relatorios.gerar.
                permission: PERMISSOES.comissoes.atividades.visualizar,
                command: () => this.aoExecutarOpcao('relatorio')
            }
        ];
    }

    aoExecutarOpcao(acao: AcaoOpcaoAtividade): void {
        if (acao === 'cadastrar') {
            this.abrirDialogNovaAtividade();
            return;
        }

        this.exportarRelatorioAtividades();
    }

    aoExecutarAcaoTabela(acao: AcaoTabelaAtividadesComissoes): void {
        switch (acao) {
            case 'editar':
                if (this.atividadeSelecionada) {
                    this.abrirDialogEditarAtividade(this.atividadeSelecionada);
                }
                break;
            case 'excluir':
                this.excluirAtividadeSelecionada();
                break;
            case 'ir-para-reuniao':
                if (this.atividadeSelecionada?.idReuniao) {
                    this.router.navigate(['/comissoes/acompanhamento'], {
                        queryParams: { reuniao: this.atividadeSelecionada.idReuniao }
                    });
                }
                break;
            case 'visualizar-comissao-nucleo':
                if (this.atividadeSelecionada?.idEstruturaOrganizacional) {
                    this.router.navigate(['/comissoes/atividades/comissoes'], {
                        queryParams: { id: this.atividadeSelecionada.idEstruturaOrganizacional }
                    });
                }
                break;
            case 'visualizar-selecionada':
                if (this.atividadeSelecionada) {
                    this.abrirDialogEditarAtividade(this.atividadeSelecionada);
                }
                break;
        }
    }

    abrirDialogNovaAtividade(idReuniao: number | null = null): void {
        this.tituloDialogAtividade = 'Nova Atividade';
        this.proximoCodigoAtividade = '';
        const hojeStr = new Date().toISOString().substring(0, 10);

        let idEstrutura: number | null = null;
        if (idReuniao) {
            const match = this.atividadesBase.find((a) => a.idReuniao === idReuniao);
            if (match) {
                idEstrutura = match.idEstruturaOrganizacional ?? null;
            }
        }

        this.formularioAtividade.setValue(
            {
                idTarefa: null,
                idEstruturaOrganizacional: idEstrutura,
                idResponsavel: null,
                sgUfResponsavel: '',
                nomeResponsavel: '',
                descricao: '',
                observacao: '',
                dataInicio: hojeStr,
                dataFim: hojeStr,
                idSituacaoTarefa: this.situacaoPadraoAtividade(),
                idReuniao: idReuniao,
                idProtocolo: null,
                resolvida: false
            },
            { emitEvent: false }
        );

        this.limparProtocoloAtividade(false);
        this.exibirDialogAtividade = true;
        this.carregarProximoCodigoAtividade();
    }

    abrirDialogEditarAtividade(a: AtividadeComissao): void {
        this.tituloDialogAtividade = 'Editar Atividade';
        this.proximoCodigoAtividade = '';

        const formatarDataInput = (d?: string | Date | null) => {
            if (!d) return '';
            const dataObj = typeof d === 'string' ? new Date(d) : d;
            return isNaN(dataObj.getTime()) ? '' : dataObj.toISOString().substring(0, 10);
        };

        this.formularioAtividade.setValue(
            {
                idTarefa: a.idTarefa ?? null,
                idEstruturaOrganizacional: a.idEstruturaOrganizacional ?? null,
                idResponsavel: a.idSolicitanteResponsavel ?? null,
                sgUfResponsavel: a.ufSolicitanteResponsavel || '',
                nomeResponsavel: a.responsavel || '',
                descricao: a.descricao || '',
                observacao: a.txObservacao || '',
                dataInicio: formatarDataInput(a.dataInicio),
                dataFim: formatarDataInput(a.dataFim),
                idSituacaoTarefa: a.idSituacaoTarefa ?? this.situacaoPadraoAtividade(),
                idReuniao: a.idReuniao ?? null,
                idProtocolo: a.idProtocolo ?? null,
                resolvida: a.status === 'resolvido'
            },
            { emitEvent: false }
        );

        this.protocoloAtividadeSelecionado = a.numeroProtocolo || (a.idProtocolo ? String(a.idProtocolo) : '');
        this.erroPesquisaProtocoloAtividade = '';
        this.exibirDialogAtividade = true;
    }

    carregarProximoCodigoAtividade(): void {
        this.comissaoAtividadeService.consultarProximoCodigoAtividade().subscribe({
            next: (response) => {
                this.proximoCodigoAtividade = response.codigoTarefa || (response.idTarefa ? this.formatarCodigo(response.idTarefa) : '');
                this.cdr.markForCheck();
            },
            error: () => {
                this.proximoCodigoAtividade = '';
                this.cdr.markForCheck();
            }
        });
    }

    fecharDialogAtividade(): void {
        this.exibirDialogAtividade = false;
    }

    abrirPesquisaProtocoloAtividade(): void {
        this.exibirDialogProtocolos = true;
        this.erroPesquisaProtocoloAtividade = '';
        this.filtroTipoProtocolo = 0;
        this.filtroNuProtocolo = '';
        this.filtroAnoProtocolo = '';
        this.protocolosPesquisa = [];
        this.protocoloPesquisaSelecionado = null;
        this.carregarTiposProtocolo();
    }

    fecharPesquisaProtocoloAtividade(): void {
        this.exibirDialogProtocolos = false;
    }

    pesquisarProtocolosAtividade(): void {
        this.carregandoProtocolos = true;
        this.erroPesquisaProtocoloAtividade = '';
        this.protocoloPesquisaSelecionado = null;

        this.consultaProtocoloSub?.unsubscribe();
        this.consultaProtocoloSub = this.protocoloService
            .pesquisar({
                idTipoProtocolo: this.filtroTipoProtocolo || undefined,
                nuProtocolo: this.parseNumeroPositivo(this.filtroNuProtocolo) ?? undefined,
                nuAnoProtocolo: this.parseNumeroPositivo(this.filtroAnoProtocolo) ?? undefined
            })
            .subscribe({
                next: (protocolos) => {
                    this.protocolosPesquisa = this.ordenarProtocolosLista(protocolos ?? []);
                    this.carregandoProtocolos = false;
                    this.cdr.markForCheck();
                },
                error: () => {
                    this.protocolosPesquisa = [];
                    this.erroPesquisaProtocoloAtividade = 'Não foi possível pesquisar os protocolos.';
                    this.carregandoProtocolos = false;
                    this.cdr.markForCheck();
                }
            });
    }

    selecionarProtocoloPesquisa(protocolo: ProtocoloSelecaoResponse): void {
        this.protocoloPesquisaSelecionado = protocolo;
    }

    confirmarSelecaoProtocoloAtividade(): void {
        if (!this.protocoloPesquisaSelecionado?.idProtocolo) {
            return;
        }

        this.formularioAtividade.patchValue({ idProtocolo: this.protocoloPesquisaSelecionado.idProtocolo });
        this.protocoloAtividadeSelecionado = this.formatarProtocolo(this.protocoloPesquisaSelecionado);
        this.erroPesquisaProtocoloAtividade = '';
        this.fecharPesquisaProtocoloAtividade();
    }

    ordenarProtocolos(campo: CampoOrdenacaoProtocolo): void {
        if (this.ordenacaoProtocoloCampo === campo) {
            this.ordenacaoProtocoloDesc = !this.ordenacaoProtocoloDesc;
        } else {
            this.ordenacaoProtocoloCampo = campo;
            this.ordenacaoProtocoloDesc = false;
        }

        this.protocolosPesquisa = this.ordenarProtocolosLista(this.protocolosPesquisa);
    }

    limparProtocoloAtividade(limparCampo = true): void {
        if (limparCampo) {
            this.formularioAtividade.patchValue({ idProtocolo: null });
        }
        this.protocoloAtividadeSelecionado = '';
        this.erroPesquisaProtocoloAtividade = '';
        this.protocoloPesquisaSelecionado = null;
    }

    private carregarTiposProtocolo(): void {
        if (this.tiposProtocolo.length > 0 || this.carregandoTiposProtocolo) {
            return;
        }

        this.carregandoTiposProtocolo = true;
        this.consultaTiposProtocoloSub?.unsubscribe();
        this.consultaTiposProtocoloSub = this.protocoloService.listarTipos().subscribe({
            next: (tipos) => {
                this.tiposProtocolo = [{ idTipoProtocolo: 0, dsTipoProtocolo: 'TODOS' }, ...(tipos ?? [])];
                this.carregandoTiposProtocolo = false;
                this.cdr.markForCheck();
            },
            error: () => {
                this.tiposProtocolo = [{ idTipoProtocolo: 0, dsTipoProtocolo: 'TODOS' }];
                this.erroPesquisaProtocoloAtividade = 'Não foi possível carregar os tipos de protocolo.';
                this.carregandoTiposProtocolo = false;
                this.cdr.markForCheck();
            }
        });
    }

    salvarAtividade(): void {
        const formVal = this.formularioAtividade.getRawValue();
        if (!formVal.descricao.trim() || !formVal.idResponsavel || !formVal.dataInicio || !formVal.idEstruturaOrganizacional) {
            return;
        }

        const request: ComissaoAtividadeSalvarRequest = {
            idEstruturaOrganizacional: formVal.idEstruturaOrganizacional,
            idSolicitanteResponsavel: formVal.idResponsavel,
            sgUfSolicitanteResponsavel: formVal.sgUfResponsavel,
            dsTarefa: formVal.descricao.trim(),
            txObservacao: formVal.observacao,
            dtInicio: formVal.dataInicio ? formVal.dataInicio + 'T00:00:00' : undefined,
            dtTermino: formVal.dataFim ? formVal.dataFim + 'T23:59:59' : undefined,
            idSituacaoTarefa: formVal.idSituacaoTarefa ?? this.situacaoPadraoAtividade() ?? undefined,
            idReuniao: formVal.idReuniao ?? undefined,
            idProtocolo: formVal.idProtocolo ?? undefined
        };

        const obs: Observable<unknown> = formVal.idTarefa ? this.comissaoAtividadeService.atualizarAtividade(formVal.idTarefa, request) : this.comissaoAtividadeService.cadastrarAtividade(request);

        obs.subscribe({
            next: () => {
                this.ultimaAcaoExecutada = formVal.idTarefa ? 'Atividade atualizada com sucesso.' : 'Atividade cadastrada com sucesso.';
                this.exibirDialogAtividade = false;
                this.carregarAtividades(this.filtrosAtivos, this.paginaAtual(), this.linhasPorPaginaAtividades);
                this.cdr.markForCheck();
            },
            error: () => {
                this.ultimaAcaoExecutada = 'Não foi possível salvar a atividade.';
                this.cdr.markForCheck();
            }
        });
    }

    abrirPesquisaResponsavelCadastro(): void {
        this.exibirDialogResponsaveis = true;
        this.filtroResponsavelUf = 'BR';
        this.filtroResponsavelNome = '';
        this.responsaveisCadastro = [];
        this.pesquisarResponsaveisCadastro();
    }

    fecharPesquisaResponsavelCadastro(): void {
        this.exibirDialogResponsaveis = false;
    }

    pesquisarResponsaveisCadastro(): void {
        this.carregandoResponsaveisCadastro = true;
        this.erroPesquisaResponsaveisCadastro = '';

        const request: ComissaoAtividadeResponsavelFiltroRequest = {
            idReuniao: this.formularioAtividade.controls.idReuniao.value ?? undefined,
            idEstruturaOrganizacional: this.formularioAtividade.controls.idEstruturaOrganizacional.value ?? undefined,
            nmSolicitante: this.filtroResponsavelNome || undefined,
            ufSgUf: this.filtroResponsavelUf === 'BR' ? undefined : this.filtroResponsavelUf
        };

        this.comissaoAtividadeService.listarResponsaveisAtividade(request).subscribe({
            next: (responsaveis) => {
                this.responsaveisCadastro = responsaveis ?? [];
                this.carregandoResponsaveisCadastro = false;
                this.cdr.markForCheck();
            },
            error: () => {
                this.responsaveisCadastro = [];
                this.erroPesquisaResponsaveisCadastro = 'Não foi possível carregar os responsáveis.';
                this.carregandoResponsaveisCadastro = false;
                this.cdr.markForCheck();
            }
        });
    }

    selecionarResponsavelCadastro(resp: ComissaoAtividadeResponsavelResponse): void {
        this.formularioAtividade.patchValue({
            idResponsavel: resp.idSolicitante ?? null,
            sgUfResponsavel: resp.ufSgUf || '',
            nomeResponsavel: resp.nmSolicitante || ''
        });
        this.fecharPesquisaResponsavelCadastro();
    }

    alternarFiltros(): void {
        this.filtrosVisiveis.update((visivel) => !visivel);
    }

    /**
     * Ao estreitar, a coluna lateral não cabe e os filtros fecham. Alargar de volta não os
     * reabre: quem os abriu foi o usuário, e reabrir sozinho desfaria a escolha dele.
     */
    aoRedimensionarJanela(): void {
        const estreita = window.innerWidth <= 1200;
        if (estreita === this.telaEstreitaFiltros) {
            return;
        }
        this.telaEstreitaFiltros = estreita;
        if (estreita) {
            this.filtrosVisiveis.set(false);
        }
    }

    private exportarRelatorioAtividades(): void {
        this.exportarAtividadesSub?.unsubscribe();
        this.exportarAtividadesSub = this.comissaoAtividadeService.exportarAtividades(this.mapearFiltrosParaRequest(this.filtrosAtivos)).subscribe({
            next: (blob) => {
                this.baixarBlob(blob, 'atividades-comissoes.csv');
                this.ultimaAcaoExecutada = 'Relatório de atividades gerado.';
                this.cdr.markForCheck();
            },
            error: () => {
                this.ultimaAcaoExecutada = 'Não foi possível gerar o relatório de atividades.';
                this.cdr.markForCheck();
            }
        });
    }

    private baixarBlob(blob: Blob, nomeArquivo: string): void {
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = nomeArquivo;
        link.click();
        window.URL.revokeObjectURL(url);
    }

    private excluirAtividadeSelecionada(): void {
        const atividade = this.atividadeSelecionada;
        if (!atividade?.idTarefa) {
            return;
        }
        if (atividade.status === 'resolvido') {
            this.messageService.add({ severity: 'warn', summary: 'Atenção', detail: 'A atividade já foi realizada. Não é possível excluir!' });
            return;
        }
        this.confirmationService.confirm({
            message: `Excluir a atividade ${atividade.codigo}?`,
            header: 'Confirmar exclusão',
            icon: 'pi pi-exclamation-triangle',
            acceptLabel: 'Sim',
            rejectLabel: 'Não',
            acceptButtonStyleClass: 'p-button-danger',
            accept: () => {
                this.excluirAtividadeSub?.unsubscribe();
                this.excluirAtividadeSub = this.comissaoAtividadeService.excluirAtividade(atividade.idTarefa!).subscribe({
                    next: () => {
                        this.ultimaAcaoExecutada = `Atividade ${atividade.codigo} excluída.`;
                        this.carregarAtividades(this.filtrosAtivos, this.paginaAtual(), this.linhasPorPaginaAtividades);
                        this.cdr.markForCheck();
                    },
                    error: () => {
                        this.ultimaAcaoExecutada = 'Não foi possível excluir a atividade.';
                        this.cdr.markForCheck();
                    }
                });
            }
        });
    }

    private carregarAtividades(filtros: FiltrosAtividadesComissoes, pagina: number, tamanhoPagina: number): void {
        this.filtrosAtivos = { ...filtros };
        this.atividadeSelecionada = null;
        this.erroConsulta = '';
        this.carregandoAtividades = true;

        const paginaNormalizada = this.normalizarInteiroNaoNegativo(pagina);
        const tamanhoPaginaNormalizado = this.normalizarLinhasPorPagina(tamanhoPagina);
        this.primeiraLinhaAtividades = paginaNormalizada * tamanhoPaginaNormalizado;
        this.linhasPorPaginaAtividades = tamanhoPaginaNormalizado;

        this.consultaAtividadesSub?.unsubscribe();
        this.consultaAtividadesSub = this.comissaoAtividadeService.consultarAtividades(this.mapearFiltrosParaRequest(filtros), paginaNormalizada, tamanhoPaginaNormalizado).subscribe({
            next: (pagina) => {
                const atividadesMapeadas = (pagina.content ?? []).map((item) => this.mapearAtividade(item));
                this.atividadesBase = atividadesMapeadas;
                this.atividadesFiltradas = this.aplicarFiltrosComplementares(atividadesMapeadas, filtros);
                this.totalRegistrosAtividades = this.normalizarInteiroNaoNegativo(pagina.totalElements ?? atividadesMapeadas.length);
                this.carregandoAtividades = false;
                this.cdr.markForCheck();
            },
            error: () => {
                this.atividadesBase = [];
                this.atividadesFiltradas = [];
                this.totalRegistrosAtividades = 0;
                this.erroConsulta = 'Não foi possível carregar as atividades de comissões.';
                this.carregandoAtividades = false;
                this.cdr.markForCheck();
            }
        });

        /**
         * Os indicadores de pendentes/resolvidas precisam do conjunto inteiro, não da página: vão
         * ao lado de um total server-side e seriam lidos como se falassem da mesma população. Vai no
         * MESMO filtro da listagem — e no mesmo escopo do `totalElements` que alimenta o total, que
         * também ignora os filtros complementares aplicados no cliente. Falhou, os indicadores
         * voltam a "–": melhor a ausência do que um número de outra origem.
         */
        this.consultaSituacoesResumoSub?.unsubscribe();
        this.consultaSituacoesResumoSub = this.comissaoEstatisticaService.listarAtividadesPorSituacao(this.mapearFiltrosParaRequest(filtros)).subscribe({
            next: (situacoes) => {
                this.situacoesResumoAtividades = situacoes ?? [];
                this.cdr.markForCheck();
            },
            error: () => {
                this.situacoesResumoAtividades = null;
                this.cdr.markForCheck();
            }
        });
    }

    private mapearFiltrosParaRequest(filtros: FiltrosAtividadesComissoes): ComissaoAtividadeFiltroRequest {
        const request: ComissaoAtividadeFiltroRequest = {
            tipoPesquisa: this.mapearTipoPesquisa(filtros.tipoSelecao)
        };

        if (filtros.tipoSelecao !== 'todos') {
            const codigoPesquisa = this.parseNumeroPositivo(filtros.valorSelecao);
            if (codigoPesquisa !== null) {
                request.codigoPesquisa = codigoPesquisa;
            }
        }

        if (filtros.aberturaInicio) {
            request.dataInicioAbertura = filtros.aberturaInicio;
        }

        if (filtros.aberturaFim) {
            request.dataFimAbertura = filtros.aberturaFim;
        }

        if (filtros.encerramentoInicio) {
            request.dataInicioEncerramento = filtros.encerramentoInicio;
        }

        if (filtros.encerramentoFim) {
            request.dataFimEncerramento = filtros.encerramentoFim;
        }

        const descricaoTarefa = filtros.descricao.trim();
        if (descricaoTarefa) {
            request.descricaoTarefa = descricaoTarefa;
        }

        if (filtros.responsavelId && filtros.responsavelUf) {
            request.idSolicitanteResponsavel = filtros.responsavelId;
            request.ufSolicitanteResponsavel = this.normalizarTexto(filtros.responsavelUf);
        }

        const idSituacaoTarefa = this.mapearIdSituacaoTarefa(filtros.situacao);
        if (idSituacaoTarefa !== null) {
            request.idSituacaoTarefa = idSituacaoTarefa;
        }

        const idTipoComissaoCt = this.mapearIdTipoComissaoCt(filtros.tipoComissao);
        if (idTipoComissaoCt !== null) {
            request.idTipoComissaoCt = idTipoComissaoCt;
        }

        return request;
    }

    private mapearTipoPesquisa(tipoSelecao: TipoSelecaoAtividade): EComissaoAtividadeTipoPesquisaFiltro {
        const mapa: Record<TipoSelecaoAtividade, EComissaoAtividadeTipoPesquisaFiltro> = {
            todos: EComissaoAtividadeTipoPesquisaFiltro.TODOS,
            'codigo-atividade': EComissaoAtividadeTipoPesquisaFiltro.CODIGO_ATIVIDADE,
            'numero-atividade': EComissaoAtividadeTipoPesquisaFiltro.NUMERO_ATIVIDADE,
            'codigo-reuniao': EComissaoAtividadeTipoPesquisaFiltro.CODIGO_REUNIAO,
            'codigo-comissao': EComissaoAtividadeTipoPesquisaFiltro.CODIGO_COMISSAO,
            'codigo-protocolo': EComissaoAtividadeTipoPesquisaFiltro.CODIGO_PROTOCOLO
        };
        return mapa[tipoSelecao];
    }

    private mapearIdTipoComissaoCt(tipoComissao: FiltrosAtividadesComissoes['tipoComissao']): number | null {
        const mapa: Record<Exclude<FiltrosAtividadesComissoes['tipoComissao'], 'todas'>, number> = {
            'comissao-interna': 1,
            'camara-tecnica-interna': 2,
            'comissao-camara-externa': 3,
            'grupo-trabalho-interno': 4
        };

        if (tipoComissao === 'todas') {
            return null;
        }

        return mapa[tipoComissao];
    }

    private mapearIdSituacaoTarefa(situacao: FiltrosAtividadesComissoes['situacao']): number | null {
        const mapa: Record<Exclude<FiltrosAtividadesComissoes['situacao'], 'todas'>, number> = {
            pendente: 1,
            resolvido: 2
        };

        if (situacao === 'todas') {
            return null;
        }

        return mapa[situacao];
    }

    private mapearAtividade(item: ComissaoAtividadeResponse): AtividadeComissao {
        const numeroAtividade = item.numeroTarefa || this.formatarCodigo(item.nuSequencialTarefa);

        return {
            codigo: item.codigoTarefa || this.formatarCodigo(item.idTarefa),
            status: this.mapearStatusAtividade(item.dsSituacaoTarefa),
            atividade: numeroAtividade,
            numeroAtividade,
            codigoReuniao: item.codigoReuniao || this.formatarCodigo(item.idReuniao),
            dataInicio: this.converterData(item.dtInicio),
            dataFim: this.converterData(item.dtTermino),
            comissaoNucleo: item.dsEstruturaOrganizacional || '-',
            responsavel: item.nmSolicitanteResponsavel || '-',
            descricao: item.dsTarefa || item.txObservacao || '-',
            tipoComissao: this.mapearTipoComissao(item.idTipoComissaoCt, item.dsEstruturaOrganizacional),
            idTarefa: item.idTarefa ?? undefined,
            idReuniao: item.idReuniao ?? undefined,
            idSituacaoTarefa: item.idSituacaoTarefa ?? undefined,
            descricaoSituacao: item.dsSituacaoTarefa ?? undefined,
            idTipoComissaoCt: item.idTipoComissaoCt ?? undefined,
            idSolicitanteResponsavel: item.idSolicitanteResponsavel ?? undefined,
            ufSolicitanteResponsavel: item.sgUfSolicitanteResponsavel ?? undefined,
            idEstruturaOrganizacional: item.idEstruturaOrganizacional ?? undefined,
            idProtocolo: item.idProtocolo ?? undefined,
            numeroProtocolo: item.numeroProtocolo ?? undefined,
            txObservacao: item.txObservacao
        };
    }

    private aplicarFiltrosComplementares(atividades: AtividadeComissao[], filtros: FiltrosAtividadesComissoes): AtividadeComissao[] {
        return atividades.filter((atividade) => this.passaNosFiltros(atividade, filtros));
    }

    private passaNosFiltros(atividade: AtividadeComissao, filtros: FiltrosAtividadesComissoes): boolean {
        if (!this.passaFiltroSelecao(atividade, filtros.tipoSelecao, filtros.valorSelecao)) {
            return false;
        }

        if (!this.passaFiltroPeriodo(atividade.dataInicio, filtros.aberturaInicio, filtros.aberturaFim)) {
            return false;
        }

        if (!this.passaFiltroPeriodo(atividade.dataFim, filtros.encerramentoInicio, filtros.encerramentoFim)) {
            return false;
        }

        if (filtros.situacao !== 'todas' && atividade.status !== filtros.situacao) {
            return false;
        }

        if (filtros.responsavelId && filtros.responsavelUf) {
            if (atividade.idSolicitanteResponsavel !== filtros.responsavelId || this.normalizarTexto(atividade.ufSolicitanteResponsavel) !== this.normalizarTexto(filtros.responsavelUf)) {
                return false;
            }
        } else if (filtros.responsavelNome && !atividade.responsavel.toLowerCase().includes(filtros.responsavelNome.trim().toLowerCase())) {
            return false;
        }

        if (filtros.descricao && !atividade.descricao.toLowerCase().includes(filtros.descricao.trim().toLowerCase())) {
            return false;
        }

        if (filtros.tipoComissao !== 'todas' && atividade.tipoComissao !== filtros.tipoComissao) {
            return false;
        }

        return true;
    }

    private passaFiltroSelecao(atividade: AtividadeComissao, tipoSelecao: TipoSelecaoAtividade, valor: string): boolean {
        const busca = valor.trim().toLowerCase();
        if (!busca || tipoSelecao === 'todos') {
            return true;
        }

        switch (tipoSelecao) {
            case 'codigo-atividade':
                return atividade.codigo.toLowerCase().includes(busca);
            case 'numero-atividade':
                return atividade.numeroAtividade.toLowerCase().includes(busca);
            case 'codigo-reuniao':
                return atividade.codigoReuniao.toLowerCase().includes(busca);
            case 'codigo-comissao':
                return String(atividade.idEstruturaOrganizacional ?? '').includes(busca);
            case 'codigo-protocolo':
                return String(atividade.idProtocolo ?? '').includes(busca) || (atividade.numeroProtocolo ?? '').toLowerCase().includes(busca);
            default:
                return true;
        }
    }

    private passaFiltroPeriodo(dataAtividade: Date | null, inicioTexto: string, fimTexto: string): boolean {
        if (!inicioTexto && !fimTexto) {
            return true;
        }

        if (!dataAtividade) {
            return false;
        }

        const data = this.inicioDia(dataAtividade).getTime();
        const inicio = inicioTexto ? this.inicioDia(new Date(`${inicioTexto}T00:00:00`)).getTime() : Number.MIN_SAFE_INTEGER;
        const fim = fimTexto ? this.fimDia(new Date(`${fimTexto}T00:00:00`)).getTime() : Number.MAX_SAFE_INTEGER;
        return data >= inicio && data <= fim;
    }

    private inicioDia(data: Date): Date {
        const copia = new Date(data);
        copia.setHours(0, 0, 0, 0);
        return copia;
    }

    private fimDia(data: Date): Date {
        const copia = new Date(data);
        copia.setHours(23, 59, 59, 999);
        return copia;
    }

    private mapearStatusAtividade(descricaoSituacao?: string): AtividadeComissao['status'] {
        const situacao = this.normalizarTexto(descricaoSituacao);
        if (situacao.includes('RESOL') || situacao.includes('CONCL') || situacao.includes('ENCERR')) {
            return 'resolvido';
        }
        return 'pendente';
    }

    private situacaoPadraoAtividade(): number | null {
        return this.situacoesAtividade[0]?.idSituacaoTarefa ?? 1;
    }

    private mapearTipoComissao(idTipoComissaoCt?: number, descricaoEstrutura?: string): AtividadeComissao['tipoComissao'] {
        if (idTipoComissaoCt === 1) {
            return 'comissao-interna';
        }

        if (idTipoComissaoCt === 2) {
            return 'camara-tecnica-interna';
        }

        if (idTipoComissaoCt === 3) {
            return 'comissao-camara-externa';
        }

        if (idTipoComissaoCt === 4) {
            return 'grupo-trabalho-interno';
        }

        const descricao = this.normalizarTexto(descricaoEstrutura);

        if (descricao.includes('GRUPO')) {
            return 'grupo-trabalho-interno';
        }

        if (descricao.includes('CAMARA')) {
            return 'camara-tecnica-interna';
        }

        if (descricao.includes('EXTERNA') || descricao.includes('EXTERNO')) {
            return 'comissao-camara-externa';
        }

        return 'comissao-interna';
    }

    private parseNumeroPositivo(valor?: string | null): number | null {
        if (!valor) {
            return null;
        }

        const numero = Number(valor);
        if (!Number.isFinite(numero) || numero <= 0) {
            return null;
        }

        return Math.trunc(numero);
    }

    private converterData(valorData?: string): Date | null {
        if (!valorData) {
            return null;
        }

        const data = new Date(valorData);
        return Number.isNaN(data.getTime()) ? null : data;
    }

    private formatarCodigo(valor?: number): string {
        if (!valor || !Number.isFinite(valor) || valor <= 0) {
            return '-';
        }
        return String(Math.trunc(valor)).padStart(6, '0');
    }

    private normalizarInteiroNaoNegativo(valor?: number | null): number {
        if (typeof valor !== 'number' || !Number.isFinite(valor)) {
            return 0;
        }

        return Math.max(0, Math.trunc(valor));
    }

    private normalizarLinhasPorPagina(valor?: number | null): number {
        const linhas = this.normalizarInteiroNaoNegativo(valor);
        return linhas > 0 ? linhas : 20;
    }

    private paginaAtual(): number {
        return Math.trunc(this.primeiraLinhaAtividades / this.linhasPorPaginaAtividades);
    }

    private reiniciarPaginacaoAtividades(): void {
        this.primeiraLinhaAtividades = 0;
        this.linhasPorPaginaAtividades = 20;
    }

    private normalizarTexto(valor?: string | null): string {
        return (valor ?? '').trim().toUpperCase();
    }

    private formatarProtocolo(protocolo: ProtocoloResponse | ProtocoloSelecaoResponse): string {
        const numero = (protocolo.nuProtocolo ?? '').trim();
        const ano = (protocolo.nuAnoProtocolo ?? '').trim();
        if (!numero && protocolo.idProtocolo) {
            return String(protocolo.idProtocolo);
        }

        const numeroFormatado = numero ? numero.padStart(6, '0') : String(protocolo.idProtocolo ?? '');
        return ano ? `${numeroFormatado}/${ano}` : numeroFormatado;
    }

    private ordenarProtocolosLista(protocolos: ProtocoloSelecaoResponse[]): ProtocoloSelecaoResponse[] {
        const campo = this.ordenacaoProtocoloCampo;
        const direcao = this.ordenacaoProtocoloDesc ? -1 : 1;

        return [...protocolos].sort((a, b) => {
            const valorA = this.valorOrdenacaoProtocolo(a, campo);
            const valorB = this.valorOrdenacaoProtocolo(b, campo);
            return valorA.localeCompare(valorB, 'pt-BR', { numeric: true, sensitivity: 'base' }) * direcao;
        });
    }

    private valorOrdenacaoProtocolo(protocolo: ProtocoloSelecaoResponse, campo: CampoOrdenacaoProtocolo): string {
        return String(protocolo[campo] ?? '');
    }
}
